import { nakshatras } from "@ishvaram/domain-data";

/**
 * Every written Devanagari form of a nakshatra's romanised pada letter, read
 * from the engine's own pada table (nakshatras.yaml syllable_hi plus
 * syllable_hi_variants via @ishvaram/domain-data) — the same table
 * /hi/baby-names/ renders below the tool, so the result can never show a
 * different akshara.
 *
 * The variants are the forms the listed names actually start with under the
 * one-sound convention (ड/द, ट/त, ठ/थ, ढ/ध, ण/न, ष/श, ञ/द; ऋ = रि; vowel length
 * not distinguished): आश्लेषा "Di" is ["डी", "दी"]. Two padas of one nakshatra
 * can share a romanisation (Purva Ashadha "Dha" is ध and ढ); both are returned.
 * @internal
 */
export function padaSyllableFormsHi(
  nakshatraId: string | null | undefined,
  letter: string
): string[] {
  const nakshatra = nakshatras.find((n) => n.id === nakshatraId);
  const wanted = letter.toLowerCase();
  return [
    ...new Set(
      (nakshatra?.padas ?? [])
        .filter((p) => p.syllable_en.toLowerCase() === wanted && p.syllable_hi)
        .flatMap((p) => [p.syllable_hi as string, ...(p.syllable_hi_variants ?? [])])
    ),
  ];
}

/**
 * The pada syllable as the table shows it ("डी / दी"). An unknown letter falls
 * back to the romanised value rather than inventing one.
 */
export function padaSyllableHi(nakshatraId: string | null | undefined, letter: string): string {
  const forms = padaSyllableFormsHi(nakshatraId, letter);
  return forms.length > 0 ? forms.join(" / ") : letter;
}

/**
 * The one form a given name starts with (दिशा → "दी" under आश्लेषा "Di"), so a
 * name card never says "अक्षर: डी" above a name written with द. Falls back to
 * the full table form when no single form shares the name's first letter.
 */
export function nameSyllableHi(
  nakshatraId: string | null | undefined,
  letter: string,
  nameHi: string | null | undefined
): string {
  const first = nameHi?.[0];
  const match = first ? padaSyllableFormsHi(nakshatraId, letter).filter((f) => f[0] === first) : [];
  return match.length === 1 ? match[0] : padaSyllableHi(nakshatraId, letter);
}
